import React, { useContext, useEffect, useState } from 'react';
import { filter, find, map, join, isEmpty, compact, flatten, includes } from 'lodash';
import { FocusBaseContext, MarkupContext } from '../../contexts';
import { createRegionRequest, deleteRegionRequest, updateRegionRequest } from '../../requests';
import { stringifyFilterData } from "../../../../../express/researcher/common/helpers";
import RegionPopup from './regionSettings/regionPopup';

const defaultHeatmapValues = { resolution: 50, threshold: 50, opacity: 0.008 };
const defaultClipMode = 'covered';

const MarkupDataProvider = ({ initTab, children }) => {
  const {
    currentSummary, heatmap, refreshRegionData, conceptById, selectedConceptId,
    updateSelectedRegionId, saveUserSettings, markupTool, onMarkupSettingsChange,
    getConceptSetting, setConceptSetting, setRegions, focus, markupType,
    aiSummaries, setAiSummaries
  } = useContext(FocusBaseContext);

  const [ tab, setTab ] = useState(initTab || 0);
  const [ createMode, setCreateMode ] = useState(false);
  const updateCreateMode = (value) => {
    markupTool.deselectComments();
    setCreateMode(value);
  };
  const [ loading, setLoading ] = useState(false);
  const [ changeConceptStage, setChangeConceptStage ] = useState(null);
  const [ clearNewRegion, setClearRegion ] = useState(null);
  const [ regionModal, setRegionModal ] = useState(null);
  const [ selectedSquare, setSelectedSquare ] = useState({ x: 0, y: 0 });

  const findThreshold = () => (
    getConceptSetting('heatmap_threshold') || defaultHeatmapValues.threshold
  );
  const [ threshold, setThreshold ] = useState(findThreshold);
  const updateThreshold = (value, saveData) => {
    setThreshold(value);
    onMarkupSettingsChange();
    if (saveData) {
      setConceptSetting('heatmap_threshold', value);
    }
  };
  useEffect(() => {
    setThreshold(findThreshold());
  }, [ currentSummary, heatmap ]);

  const findResolution = () => (
    getConceptSetting('heatmap_resolution') || defaultHeatmapValues.resolution
  );
  const [ resolution, setResolution ] = useState(findResolution);
  const updateResolution = (value, saveData) => {
    setResolution(value);
    onMarkupSettingsChange();
    if (saveData) {
      setConceptSetting('heatmap_resolution', value);
    }
  };
  useEffect(() => {
    setResolution(findResolution());
  }, [ currentSummary, heatmap ]);

  const findOpacity = () => (getConceptSetting('markup_opacity') || defaultHeatmapValues.opacity);
  const [ opacity, setOpacity ] = useState(findOpacity);
  const updateOpacity = (value, saveData) => {
    setOpacity(value);
    onMarkupSettingsChange();
    if (saveData) {
      setConceptSetting('markup_opacity', value);
    }
  };
  useEffect(() => {
    setOpacity(findOpacity());
  }, [ currentSummary, heatmap ]);

  const findConceptData = () => (
    find(
      (currentSummary.report?.concepts || []),
      (el) => (el.id === selectedConceptId())
    ) || {}
  );
  const [ conceptData, setConceptData ] = useState(findConceptData);
  const [ conceptImage, setConceptImage ] = useState(() => (conceptData?.src));
  useEffect(() => {
    const newConcept = findConceptData();
    setConceptData(newConcept);
    setConceptImage(newConcept?.src);
  }, [ currentSummary, heatmap ]);


  const allowedReactions = (currentSummary.allowed_reactions);
  const allowedTools = (currentSummary.allowed_tools);

  const prepareOffsets = (event) => {
    if (event.offsetX || !event.touches) {
      return { offsetX: event.offsetX, offsetY: event.offsetY };
    }
    const bodyData = document.getElementsByTagName('body')[0].getBoundingClientRect();
    const elBox = event.touches[0].target.getBoundingClientRect();
    const offsetX = event.touches[0].pageX - elBox.left;
    const offsetY = event.touches[0].pageY - elBox.top + bodyData.top;
    return { offsetX, offsetY };
  };

  const handleAreaClick = (event) => {
    const offsets = prepareOffsets(event.nativeEvent);
    markupTool.areaClick(offsets.offsetX, offsets.offsetY);
    onMarkupSettingsChange();
  };

  const deselectComments = (event) => {
    markupTool.deselectComments();
    onMarkupSettingsChange();
  };

  const clearNewRegionSelection = () => {
    setClearRegion({});
  };

  const currentFilters = () => {
    const filterOptions = compact(
      flatten(
        map(
          focus.filters,
          (el) => filter(
            el.options || [],
            (item) => includes(currentSummary.filter, item.value)
          )
        )
      )
    );

    return isEmpty(filterOptions) ? "none" : join(map(filterOptions, (el) => el.label), ",");
  };

  const contextValue = {
    changeConceptStage, setChangeConceptStage,
    threshold, updateThreshold,
    resolution, updateResolution,
    opacity, updateOpacity,
    loading, setLoading,
    allowedReactions, allowedTools, tab, setTab, createMode, updateCreateMode,
    setRegionModal, selectedSquare, deselectComments, currentFilters,
    conceptData, conceptImage, clearNewRegion, handleAreaClick, setSelectedSquare
  };

  const handleOnClose = () => {
    setRegionModal(null);
  };

  const handleOnCreate = (region, data) => {
    const concept = conceptById();
    const filters = currentFilters();
    createRegionRequest(
      concept.create_region_path,
      {
        name: data.name, data: region.data, clip_mode: defaultClipMode, concept_id: concept.id,
        positive: markupTool.reactions.positive, negative: markupTool.reactions.negative,
        confusing: markupTool.reactions.confusing, markup_type: markupType,
        filters, filter_data: stringifyFilterData([ ...currentSummary.filter ])
      },
      (response) => {
        concept.regions = response.data.regions;
        const newRegionId = response.data.new_region_id;
        updateSelectedRegionId(newRegionId);
        saveUserSettings({
          region_id: newRegionId,
          concept_id: concept.id
        });
        markupTool.setRegions(concept.regions, newRegionId);
        updateCreateMode(false);
        handleOnClose();
        setRegions(concept.regions);
        onMarkupSettingsChange();
      }
    );
  };

  const handleOnSave = (region, data) => {
    region.name = data.name;
    updateRegionRequest(region.update_path, { name: data.name },
      (response) => {
        setRegions(response.data.regions);
      });
    refreshRegionData();
    handleOnClose();
  };

  const handleOnDelete = (region) => {
    if (region?.id) {
      deleteRegionRequest(region.destroy_path, (response) => {
        const concept = conceptById();
        concept.regions = response.data.regions;
        const newRegionId = null;
        updateSelectedRegionId(newRegionId);
        const newSummaries = filter(aiSummaries, (item) => item.concept_region_id !== region.id);
        setAiSummaries([ ...newSummaries ]);
        saveUserSettings({
          region_id: newRegionId,
          concept_id: concept.id
        });
        markupTool.setRegions(concept.regions, newRegionId);
        handleOnClose();
        setRegions(concept.regions);
        onMarkupSettingsChange();
      });
    } else {
      clearNewRegionSelection();
      handleOnClose();
    }
  };

  return (
    <MarkupContext.Provider value={ contextValue }>
      { children }
      {
        !!regionModal &&
        <RegionPopup
          region={ regionModal }
          onCreate={ handleOnCreate }
          onSave={ handleOnSave }
          onClose={ handleOnClose }
          onDelete={ handleOnDelete }
        />
      }
    </MarkupContext.Provider>
  );
};

export default MarkupDataProvider;
